import React, { useState, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import { Helmet } from 'react-helmet-async';
import BlogEmailCapture from '../../components/BlogEmailCapture';
import { useHasActiveAccess, SubscriberCtaContent } from '../../components/AdaptiveCta';
import posts from './blogData';

const cyan = '#00BFFF';
const green = '#8DC63F';

const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'ReaderFlex Blog',
    description:
        'Science-backed articles on speed reading, reading comprehension, and how to train yourself to read faster without losing understanding.',
    url: 'https://readerflex.com/blog',
    publisher: {
        '@type': 'Organization',
        name: 'ReaderFlex',
        url: 'https://readerflex.com',
    },
    blogPost: posts.map((p, i) => ({
        '@type': 'BlogPosting',
        position: i + 1,
        headline: p.title,
        description: p.description,
        datePublished: p.date,
        url: `https://readerflex.com/blog/${p.slug}`,
        author: { '@type': 'Organization', name: 'ReaderFlex' },
    })),
};

// ── Component ───────────────────────────────────────────────────────
const BlogHome = () => {
    const navigate = useNavigate();
    const hasActiveAccess = useHasActiveAccess();
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        if (!search.trim()) return posts;
        const q = search.toLowerCase();
        return posts.filter(
            (p) =>
                p.title.toLowerCase().includes(q) ||
                p.description.toLowerCase().includes(q) ||
                p.tags.some((t) => t.toLowerCase().includes(q)) ||
                p.body.toLowerCase().includes(q),
        );
    }, [search]);

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <Helmet>
                <title>ReaderFlex Blog - Speed Reading Tips, Science & Training Guides</title>
                <meta name="description" content="Explore science-backed articles on speed reading techniques, comprehension training, and how to double your reading speed with proven methods." />
                <link rel="canonical" href="https://readerflex.com/blog" />
                <meta property="og:title" content="ReaderFlex Blog - Speed Reading Tips, Science & Training Guides" />
                <meta property="og:description" content="Explore science-backed articles on speed reading techniques, comprehension training, and how to double your reading speed with proven methods." />
                <meta property="og:url" content="https://readerflex.com/blog" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="ReaderFlex" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="ReaderFlex Blog" />
                <meta name="twitter:description" content="Science-backed articles on speed reading, comprehension, and training." />
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>

            {/* ── Hero ─────────────────────────────────────────────── */}
            <Box
                component="header"
                sx={{
                    pt: { xs: 8, md: 12 },
                    pb: { xs: 6, md: 8 },
                    position: 'relative',
                    textAlign: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: (theme) =>
                            theme.palette.mode === 'dark'
                                ? `radial-gradient(ellipse at 50% 0%, ${alpha(cyan, 0.08)} 0%, transparent 55%)`
                                : `radial-gradient(ellipse at 50% 0%, ${alpha(cyan, 0.05)} 0%, transparent 55%)`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="md">
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <AutoStoriesIcon sx={{ color: cyan, fontSize: 28 }} />
                        <Typography
                            variant="overline"
                            sx={{ color: cyan, letterSpacing: 3, fontWeight: 700 }}
                        >
                            The ReaderFlex Blog
                        </Typography>
                    </Box>

                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: 'text.primary',
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            lineHeight: 1.15,
                            mb: 2,
                        }}
                    >
                        Read Faster. Understand More.{' '}
                        <Box component="span" sx={{ color: green }}>
                            Train Smarter.
                        </Box>
                    </Typography>

                    <Typography
                        variant="h6"
                        component="p"
                        sx={{
                            color: 'text.secondary',
                            fontWeight: 400,
                            maxWidth: 620,
                            mx: 'auto',
                            mb: 4,
                            lineHeight: 1.6,
                        }}
                    >
                        Science-backed guides on speed reading, comprehension, and the
                        techniques that actually move the needle. No fluff. No gimmicks.
                    </Typography>

                    {/* Search */}
                    <TextField
                        placeholder="Search articles by topic, title, or keyword..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        variant="outlined"
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            maxWidth: 560,
                            mx: 'auto',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? alpha('#fff', 0.04)
                                        : alpha('#000', 0.03),
                            },
                        }}
                    />
                </Container>
            </Box>

            {/* ── Post listing ─────────────────────────────────────── */}
            <Container
                component="section"
                maxWidth="md"
                sx={{ pb: { xs: 6, md: 10 } }}
            >
                {filtered.length === 0 && (
                    <Typography
                        variant="body1"
                        sx={{ textAlign: 'center', color: 'text.secondary', py: 8 }}
                    >
                        No articles match your search. Try a different keyword.
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {filtered.map((post) => (
                        <Card
                            key={post.slug}
                            component={RouterLink}
                            to={`/blog/${post.slug}`}
                            elevation={0}
                            sx={{
                                textDecoration: 'none',
                                borderRadius: 3,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                backgroundColor: (theme) =>
                                    theme.palette.mode === 'dark'
                                        ? alpha('#fff', 0.02)
                                        : 'background.paper',
                                transition: 'all 0.25s ease',
                                '&:hover': {
                                    borderColor: cyan,
                                    boxShadow: (theme) =>
                                        theme.palette.mode === 'dark'
                                            ? `0 0 0 1px ${alpha(cyan, 0.3)}, 0 8px 30px ${alpha(cyan, 0.08)}`
                                            : `0 0 0 1px ${alpha(cyan, 0.2)}, 0 8px 30px ${alpha('#000', 0.06)}`,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                {/* Tags */}
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                    {post.tags.map((tag) => (
                                        <Chip
                                            key={tag}
                                            label={tag}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                letterSpacing: 0.5,
                                                height: 24,
                                                backgroundColor: (theme) =>
                                                    alpha(
                                                        theme.palette.mode === 'dark' ? cyan : '#000',
                                                        theme.palette.mode === 'dark' ? 0.12 : 0.06,
                                                    ),
                                                color: (theme) =>
                                                    theme.palette.mode === 'dark' ? cyan : 'text.primary',
                                            }}
                                        />
                                    ))}
                                </Box>

                                {/* Title */}
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 1,
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {post.title}
                                </Typography>

                                {/* Description */}
                                <Typography
                                    variant="body1"
                                    sx={{ color: 'text.secondary', lineHeight: 1.7, mb: 2 }}
                                >
                                    {post.description}
                                </Typography>

                                {/* Meta row */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTimeIcon
                                            sx={{ fontSize: 16, color: 'text.disabled' }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            {post.date} &middot; {post.readMin} min read
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                ml: 1,
                                                px: 1,
                                                py: 0.2,
                                                borderRadius: 1.5,
                                                backgroundColor: (theme) =>
                                                    alpha(theme.palette.secondary.main, 0.12),
                                                border: (theme) =>
                                                    `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'secondary.main',
                                                    fontWeight: 600,
                                                    fontStyle: 'italic',
                                                    fontSize: '0.65rem',
                                                }}
                                            >
                                                for most people.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: cyan,
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                        }}
                                    >
                                        Read article <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>

            {/* ── Bottom CTA ───────────────────────────────────────── */}
            <Box
                component="aside"
                sx={{
                    py: { xs: 8, md: 10 },
                    textAlign: 'center',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: (theme) =>
                            theme.palette.mode === 'dark'
                                ? `linear-gradient(180deg, transparent 0%, ${alpha(cyan, 0.03)} 50%, transparent 100%)`
                                : `linear-gradient(180deg, transparent 0%, ${alpha(cyan, 0.02)} 50%, transparent 100%)`,
                        pointerEvents: 'none',
                    },
                }}
            >
                <Container maxWidth="sm">
                    {hasActiveAccess ? (
                        <SubscriberCtaContent
                            title="Ready to put this into practice?"
                            description="Share your affiliate link and earn $3.00/month when friends start training with ReaderFlex."
                        />
                    ) : (
                        <>
                            <Typography
                                variant="h4"
                                component="h2"
                                sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5 }}
                            >
                                Ready to put this into practice?
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                            >
                                ReaderFlex combines the Guide Dot, adaptive pacing, comprehension
                                quizzes, and a full gamification system into one focused training
                                tool. Start your $1 trial today.
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    justifyContent: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    onClick={() => navigate('/register')}
                                    sx={{ py: 1.5, px: 5 }}
                                >
                                    Start Your Trial
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="large"
                                    onClick={() => navigate('/features')}
                                    sx={{ py: 1.5, px: 5 }}
                                >
                                    See All Features
                                </Button>
                            </Box>
                        </>
                    )}
                </Container>
            </Box>

            {/* Email capture */}
            <Container maxWidth="md" sx={{ mb: 6 }}>
                <BlogEmailCapture sourcePage="blog-home" />
            </Container>
        </Box>
    );
};

export default BlogHome;
